import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function handleGoogleLogin() {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const { heroImageUrl, heroImageUrlDark, loginBannerImage, landingHeadingLine1, landingHeadingLine2 } = useCompany();
  const { theme } = useTheme();
  const activeHero = theme === "dark" ? (heroImageUrlDark || heroImageUrl) : heroImageUrl;
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Bienvenido de nuevo");
      if (u.role === "super_admin") navigate("/super-admin");
      else if (u.role === "company_admin") navigate("/admin");
      else navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white dark:bg-[#0b0b0d]">
      {/* Panel izquierdo: imagen de banner editable desde super_admin +
          texto overlay con el heading del landing (también editable). */}
      <div className="hidden md:block bg-black relative overflow-hidden">
        {loginBannerImage && (
          <img
            src={loginBannerImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-70"
            data-testid="login-banner-image"
          />
        )}
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="text-xs uppercase tracking-[0.3em] opacity-80">Bienvenido</p>
          <h2 className="mt-2 text-5xl font-display font-extrabold leading-none" data-testid="login-banner-heading">
            {landingHeadingLine1}<br />{landingHeadingLine2}
          </h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          {/* Header del form: imagen del landing (respeta light/dark)
              centrada y clickable → home. Reemplaza favicon + wordmark. */}
          {activeHero && (
            <Link to="/" className="block mx-auto mb-8 w-fit" data-testid="login-hero-link">
              <img
                src={activeHero}
                alt="Inicio"
                className="max-h-16 sm:max-h-20 w-auto max-w-full h-auto object-contain select-none"
                draggable={false}
                data-testid="login-hero-image"
              />
            </Link>
          )}
          <h1 className="text-3xl font-display font-extrabold tracking-tight">Iniciar sesión</h1>
          <p className="text-sm text-neutral-500 mt-1">Bienvenido otra vez.</p>

          <button
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            className="mt-6 w-full h-12 rounded-xl border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center gap-3 font-medium text-sm"
            type="button"
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.6 6.1 29.6 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3l5.7-5.7C34.6 6.1 29.6 4 24 4 16.4 4 9.9 8.2 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.2-5.5l-6.6-5.4C29.6 34.7 26.9 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.6 5.1C9.7 39.7 16.3 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4 5.6l6.6 5.4c4.6-4.2 7.2-10.3 7.2-17 0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            Continuar con Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-neutral-400">
            <div className="flex-1 h-px bg-neutral-200" />O<div className="flex-1 h-px bg-neutral-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Correo</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12" data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90 rounded-xl text-base font-semibold" data-testid="login-submit-btn">
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <p className="text-sm text-neutral-500 mt-6 text-center">
            ¿No tienes cuenta? <Link to="/register" className="font-semibold text-black dark:text-neutral-100" data-testid="go-register-link">Regístrate</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
